import { type IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faWarning } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cva, type VariantProps } from "class-variance-authority";
import { clsx } from "clsx";
import { type ReactNode, type PropsWithChildren } from "react";

import { Spinner } from "../assets/spinner";

export const tileContainer = cva(
  "flex size-full flex-col items-start justify-between overflow-hidden rounded-xl border border-interface-neutral-3 bg-interface-neutral-0 text-start leading-none text-interface-neutral-6 shadow-md active:bg-interface-neutral-2",
  {
    variants: {
      variant: {
        normal: "px-6 py-10",
        main: "p-10"
      },
      disabled: {
        true: "opacity-30"
      },
      showOpacity: {
        true: "opacity-30"
      }
    }
  }
);

export const tileContent = cva("flex items-center justify-center", {
  variants: {
    variant: {
      normal: "size-12",
      main: "m-auto flex-1"
    }
  }
});

export const tileTitle = cva("", {
  variants: {
    variant: {
      normal: "text-3xl font-semibold",
      main: "text-5xl font-bold"
    }
  }
});

export type TileProps = VariantProps<typeof tileContainer> & {
  title: ReactNode;
  warningIcon?: boolean;
  warning?: ReactNode;
  onClick?: () => void;
  className?: string;
  isLoading?: boolean;
} & PropsWithChildren;

export const Tile = ({
  title,
  warning,
  warningIcon = false,
  onClick,
  variant = "normal",
  disabled,
  isLoading = false,
  className,
  children,
  showOpacity
}: TileProps) => {
  return (
    <button
      type="button"
      className={clsx("relative", className)}
      onClick={onClick}
      disabled={(disabled ?? false) || isLoading}
    >
      {warningIcon ? (
        <div className="absolute right-3 top-3 z-10">
          <TileWarningIcon />
        </div>
      ) : null}

      {warning && !isLoading ? (
        <div className="absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2">
          <TileWarningMessage>{warning}</TileWarningMessage>
        </div>
      ) : null}

      {isLoading ? (
        <div className="absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2">
          <Spinner className="size-20" />
        </div>
      ) : null}

      <div
        className={tileContainer({
          variant,
          disabled,
          showOpacity: showOpacity || Boolean(warning) || isLoading
        })}
      >
        <div className={tileContent({ variant })}>{children}</div>
        <p className={tileTitle({ variant })}>{title}</p>
      </div>
    </button>
  );
};

type TileIconProps = {
  icon: IconDefinition;
};

export const TileIcon = ({ icon }: TileIconProps) => {
  return (
    <FontAwesomeIcon icon={icon} className="size-full max-h-44 text-inherit" />
  );
};

const TileWarningMessage = ({ children }: PropsWithChildren) => {
  return (
    <div className="flex items-center justify-center gap-2 rounded-3xl bg-interface-warning-1 p-3">
      <FontAwesomeIcon icon={faWarning} className="text-interface-neutral-5" />
      <p className="font-medium text-interface-neutral-6">{children}</p>
    </div>
  );
};

const TileWarningIcon = () => {
  return (
    <div className="z-10 flex size-10 items-center justify-center rounded-full bg-interface-warning-1 p-3 text-interface-neutral-5">
      <FontAwesomeIcon icon={faWarning} className="size-full" />
    </div>
  );
};
